import { faListUl } from '@fortawesome/pro-regular-svg-icons'
import { SectionWrapper } from '../SectionWrapper/SectionWrapper'
import { DetailInfo, DetailRow } from '../DetailRow/DetailRow'
import { useStyles } from './MicrosoftOfferInfo.styles'
import { dateAndTimeConversion } from '../../utils/dateAndTimeConversion'

import {
  AzureOfferPlan,
  AzurePricingType,
  PrivateOffer,
} from '../../../modules/privateOffer/types'
import { ColumnReadMore, ViewValueWithTruncate } from '@labrav/common'
import {
  getPurchaseStartDateDisplayValue,
  parseAcceptedDateMode,
  resolvePurchaseTermsCustomized,
} from '../../../modules/privateOffer/privateOfferCreation/utils/microsoftPurchaseTermsUtil'
import { PlanType } from '@labrav/react-components'
import { MicrosoftViewPricing } from '../../../components/MicrosoftViewPricing/MicrosoftViewPricing'
import { useSelector } from '../../../store'
import { Product } from '../../../modules/productsListing/types'

export type MicrosoftOfferInfoPricingProps = {
  plans?: AzureOfferPlan[]
  azurePricingType?: AzurePricingType
  planType?: PlanType
}

export const MicrosoftOfferInfo = ({
  privateOffer,
  planType,
  product,
}: {
  privateOffer?: PrivateOffer
  planType?: PlanType
  product?: Product
}) => {
  const { classes } = useStyles()
  const timeZone = useSelector<string>(
    state =>
      state.userProfile.userProfile.timeZone ||
      state.userProfile.userProfile.defaultTimeZone ||
      'UTC'
  )

  return (
    <>
      <SectionWrapper heading="Offer details" icon={faListUl}>
        <div className={classes.sectionContent}>
          <DetailRow borderBottom>
            <>
              <DetailInfo
                title="Buyer name"
                value={
                  <ViewValueWithTruncate value={privateOffer?.companyName} />
                }
                keyName="cloudOfferId"
              />
              <DetailInfo
                title="Buyer billing account ID"
                value={
                  <ViewValueWithTruncate
                    value={privateOffer?.billingAccountId}
                    showIcon={true}
                  />
                }
                keyName="labraOfferId"
              />
            </>
          </DetailRow>
          <DetailRow borderBottom>
            <>
              <DetailInfo
                title="Offer name"
                value={
                  <ViewValueWithTruncate
                    value={privateOffer?.privateOfferName}
                  />
                }
                keyName="offerName"
              />
              <DetailInfo
                title="Offer expiration date"
                value={
                  privateOffer?.acceptBy
                    ? (() => {
                        const { modifiedDate, modifiedTime } =
                          dateAndTimeConversion(
                            privateOffer?.acceptBy,
                            timeZone
                          )
                        return (
                          <div className={classes.dateTimeWrapper}>
                            <div className={classes.createdAtDate}>
                              {modifiedDate}
                            </div>
                            {modifiedTime && (
                              <div className={classes.createdAtTime}>
                                {modifiedTime.trim()}
                              </div>
                            )}
                          </div>
                        )
                      })()
                    : '-'
                }
                keyName="acceptBy"
              />
            </>
          </DetailRow>
          <DetailRow borderBottom>
            <>
              <DetailInfo
                title="Offer description"
                value={
                  privateOffer?.offerDescription ? (
                    <ColumnReadMore
                      customCharForRemoveFromMax={15}
                      initializeWithLowerCase
                      description={privateOffer?.offerDescription || ''}
                    />
                  ) : (
                    '-'
                  )
                }
                keyName="offerDescription"
              />
              <DetailInfo
                title="Product name"
                value={
                  <ViewValueWithTruncate value={privateOffer?.productName} />
                }
                keyName="productName"
              />
            </>
          </DetailRow>
          <DetailRow borderBottom>
            <>
              <DetailInfo
                title="Purchase start date"
                value={
                  parseAcceptedDateMode(privateOffer?.variableStartDate)
                    ? getPurchaseStartDateDisplayValue({
                        variableStartDate: privateOffer?.variableStartDate,
                        offerStartDate: privateOffer?.offerStartDate,
                        offerExpirationDate: privateOffer?.offerExpirationDate,
                        createdAt: privateOffer?.createdAt,
                        purchaseTermsCustomized: resolvePurchaseTermsCustomized(
                          {
                            variableStartDate: privateOffer?.variableStartDate,
                            offerStartDate: privateOffer?.offerStartDate,
                            offerExpirationDate:
                              privateOffer?.offerExpirationDate,
                            createdAt: privateOffer?.createdAt,
                            metaData: privateOffer?.metaData,
                          }
                        ),
                      })
                    : privateOffer?.offerStartDate
                      ? (() => {
                          const { modifiedDate, modifiedTime } =
                            privateOffer?.offerStartDate
                              ? dateAndTimeConversion(
                                  privateOffer?.offerStartDate,
                                  timeZone
                                )
                              : { modifiedDate: '', modifiedTime: '' }
                          return (
                            <div className={classes.dateTimeWrapper}>
                              <div className={classes.createdAtDate}>
                                {modifiedDate || '-'}
                              </div>
                              {modifiedTime && (
                                <div className={classes.createdAtTime}>
                                  {modifiedTime.trim()}
                                </div>
                              )}
                            </div>
                          )
                        })()
                      : '-'
                }
                keyName="offerStartDate"
              />
              <DetailInfo
                title="Purchase end date"
                value={
                  privateOffer?.offerExpirationDate
                    ? (() => {
                        const { modifiedDate, modifiedTime } =
                          dateAndTimeConversion(
                            privateOffer?.offerExpirationDate,
                            timeZone
                          )
                        return (
                          <div className={classes.dateTimeWrapper}>
                            <div className={classes.createdAtDate}>
                              {modifiedDate || '-'}
                            </div>
                            {modifiedTime && (
                              <div className={classes.createdAtTime}>
                                {modifiedTime.trim()}
                              </div>
                            )}
                          </div>
                        )
                      })()
                    : '-'
                }
                keyName="offerExpirationDate"
              />
            </>
          </DetailRow>
          <DetailRow borderBottom>
            <>
              <DetailInfo
                title="Auto renewal"
                value={privateOffer?.existingContractRenewal ? 'Yes' : 'No'}
                keyName="existingContractRenewal"
              />
              <DetailInfo
                title="Pricing configuration"
                value={
                  privateOffer?.azurePricingType
                    ? privateOffer?.azurePricingType ===
                      AzurePricingType?.ABSOLUTE
                      ? 'Absolute'
                      : 'Discount'
                    : '-'
                }
                keyName="azurePricingType"
              />
            </>
          </DetailRow>
        </div>
      </SectionWrapper>
      <MicrosoftViewPricing
        plans={privateOffer?.plans || []}
        azurePricingType={privateOffer?.azurePricingType}
        planType={planType}
        product={product}
      />
    </>
  )
}
